/**
 * The app's bottom play bar — the fixed sheet with the big round play/pause
 * button — together with the playback mechanics that always come with it. Shared
 * by the topic screen (a level's dialogue) and the dictionary (the saved
 * phrases), so the two can't drift apart:
 *
 *   contentHtml: `… ${playBarHtml({ label: 'Odtwórz zwroty' })}`
 *   const playback = createPlayBar(main, { list, playlist, label });
 *
 * What the controller owns, and why it is worth sharing:
 *   - **play → pause → resume** on one button, its icon and `aria-label` driven by
 *     the Player's state rather than by what we think we asked for.
 *   - the **current row highlight**: whichever `[data-sentence]` / `[data-phrase]`
 *     row the Player reports gets `bg-lilac/60` — the brand-family tint, so
 *     "this is playing" never collides with the warm surfaces around it.
 *   - **autoscroll**, armed *only* by the main button: a single-item play leaves
 *     the page where it is (the user is already looking at that row). A
 *     `followedId` guard keeps it to one scroll per clip, so pause/resume never
 *     re-centres the page.
 *   - the **resume point**: every id the Player reports that belongs to the
 *     playlist becomes the point the main button carries on from, so stopping
 *     mid-list and pressing play again picks up there instead of starting over.
 *     A run that drains on its own clears it (nothing left to resume). Screen-local
 *     on purpose — re-entering the screen starts from the top again.
 *   - **teardown on leave**: the bar belongs to its screen, so a `hashchange`
 *     stops playback and detaches.
 *
 * The playlist itself stays with the caller (`playlist()`), because only the screen
 * knows what "the list" currently is — a level's sentences, the saved phrases.
 */

import { ICONS } from './icons.js';
import { getStatus, pause, play, resume, stop, subscribe } from './player.js';

const BAR_ID = 'player-bar';
const TOGGLE_ID = 'player-toggle';

/** Rows the controller can highlight / scroll to, keyed by their own id. */
const ROW_SELECTOR = '[data-sentence], [data-phrase]';

/**
 * Markup for the bar. Starts `hidden`; {@link createPlayBar} reveals it once the
 * screen has something to play, so a failed load never shows a dead button.
 *
 * @param {{ label?: string, startId?: string, endId?: string }} options
 *   - label: `aria-label` of the play button in its idle state.
 *   - startId / endId: ids for the slots left and right of the button, for a
 *     screen that puts controls there (the topic's level pills). Omit for a bar
 *     with nothing but the centred button.
 */
export function playBarHtml({ label = 'Odtwórz', startId = '', endId = '' } = {}) {
  return `
    <div id="${BAR_ID}" hidden
      class="fixed inset-x-0 bottom-0 z-40 bg-cream/95 backdrop-blur">
      <div class="mx-auto grid w-full max-w-sm grid-cols-3 items-center gap-2 px-4 py-3"
        style="padding-bottom: calc(env(safe-area-inset-bottom) + 0.5rem);">
        <div ${startId ? `id="${startId}"` : ''} class="flex flex-wrap items-center justify-start gap-1.5"></div>
        <div class="flex justify-center">
          <button id="${TOGGLE_ID}" type="button" aria-label="${label}"
            class="-mt-6 grid h-14 w-14 place-items-center rounded-full bg-brand text-white shadow-soft-lg transition hover:bg-brand-dark active:scale-95">
            ${ICONS.play('h-7 w-7')}
          </button>
        </div>
        <div ${endId ? `id="${endId}"` : ''} class="flex flex-wrap items-center justify-end gap-1.5"></div>
      </div>
    </div>`;
}

/**
 * Bring the row being played into view. Centred on purpose: the sticky top bar and
 * the bar itself both overlap the content, and `'center'` clears both without any
 * offset arithmetic. Honours `prefers-reduced-motion`.
 */
function scrollRowIntoView(el) {
  if (!el) return;
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
}

/**
 * Wire the bar rendered by {@link playBarHtml} and reveal it.
 *
 * @param {HTMLElement} main The screen's content root (holds the bar markup).
 * @param {object} options
 *   - list: element holding the rows, for the highlight and autoscroll. Optional
 *     — omit for a screen with no rows to follow.
 *   - playlist: `() => Array<{id, blob}>` — the whole list, in play order. Called
 *     lazily and memoised until `reset()` / `invalidate()`; items without a Blob
 *     are dropped here, so callers can hand over their rows as they are. Both
 *     `reset()` and `invalidate()` call it again on the spot, so the screen must
 *     have its new list in place before it calls either.
 *   - label: `aria-label` of the button when it isn't playing.
 *   - onComplete: `({ fromStart }) => void` — the queue drained on its own
 *     (a stop, a replacing play or a failed last clip don't get here).
 *     `fromStart` tells a full listen from a mid-list resume.
 * @returns {{playOne: (id: string, blob: ?Blob) => void, reset: () => void,
 *   invalidate: () => void, hide: () => void, destroy: () => void}}
 */
export function createPlayBar(main, { list, playlist, label = 'Odtwórz', onComplete } = {}) {
  const bar = main.querySelector(`#${BAR_ID}`);
  const toggle = main.querySelector(`#${TOGGLE_ID}`);
  if (!bar || !toggle) return noop();

  /** Memoised playable items — the playlist is rebuilt only when invalidated. */
  let items = null;
  const playable = () =>
    (items ??= (playlist?.() || []).filter((item) => item.blob instanceof Blob));

  /** No clip cached anywhere in the list → no bar, rather than a dead button. */
  const syncVisibility = () => {
    bar.hidden = playable().length === 0;
  };

  let anchorId = null;
  let follow = false;
  let followedId = null;

  const rowFor = (id) =>
    list?.querySelector(`[data-sentence="${CSS.escape(id)}"], [data-phrase="${CSS.escape(id)}"]`);

  const applyState = ({ status, currentId }) => {
    // Only items of the playlist are resume points: a one-off play of something
    // outside it (a phrase on the topic screen) must not move the list's place.
    if (currentId && playable().some((item) => item.id === currentId)) anchorId = currentId;

    const playing = status === 'playing';
    toggle.innerHTML = playing ? ICONS.pause('h-7 w-7') : ICONS.play('h-7 w-7');
    toggle.setAttribute('aria-label', playing ? 'Wstrzymaj' : label);

    list?.querySelectorAll(ROW_SELECTOR).forEach((row) => {
      const id = row.dataset.sentence ?? row.dataset.phrase;
      row.classList.toggle('bg-lilac/60', Boolean(currentId) && id === currentId);
    });

    // Idle means the queue drained, was stopped or replaced — stop following
    // until the next run is started from the main button.
    if (status === 'idle') {
      follow = false;
      followedId = null;
      return;
    }
    if (!follow || !currentId || currentId === followedId) return;
    followedId = currentId;
    scrollRowIntoView(rowFor(currentId));
  };

  toggle.addEventListener('click', () => {
    const status = getStatus();
    if (status === 'playing') return pause();
    if (status === 'paused') return resume();

    // Start at the resume point; an unknown / stale one falls back to the top.
    const all = playable();
    const at = anchorId ? all.findIndex((item) => item.id === anchorId) : -1;
    const from = at === -1 ? 0 : at;
    const queue = all.slice(from);
    if (!queue.length) return;

    follow = true; // whole list → the scroll follows along
    followedId = null;
    play(queue, {
      onComplete: () => {
        anchorId = null; // heard to the end → the next press starts over
        onComplete?.({ fromStart: from === 0 });
      },
    });
  });

  const unsubscribe = subscribe(applyState);
  syncVisibility();

  /**
   * Set by {@link destroy}. Every method checks it, so a controller that has been
   * torn down is inert rather than half-alive: without this, `invalidate()` after
   * `hide()` would run `syncVisibility()` and could bring a bar back that has no
   * subscription left to drive its icon.
   */
  let destroyed = false;

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    window.removeEventListener('hashchange', destroy);
    unsubscribe();
    stop();
  }
  window.addEventListener('hashchange', destroy);

  return {
    /** Play one item on its own: no autoscroll, and it can't complete a run. */
    playOne(id, blob) {
      if (destroyed || !(blob instanceof Blob)) return;
      follow = false; // single item — leave the scroll where it is
      play([{ id, blob }]);
    },
    /**
     * The list changed for good (a level switch): end playback, forget the place
     * and rebuild the playlist. Call it **after** the new list is in place — it
     * re-reads `playlist()` here, so from the old one it would cache exactly what
     * is being replaced.
     */
    reset() {
      if (destroyed) return;
      stop();
      anchorId = null;
      items = null;
      syncVisibility();
    },
    /** The list was edited in place — rebuild it on the next press. */
    invalidate() {
      if (destroyed) return;
      items = null;
      syncVisibility();
    },
    /** Nothing left to play on this screen: stop and take the bar away. */
    hide() {
      destroy();
      bar.hidden = true;
    },
    destroy,
  };
}

/** Stand-in for a screen whose bar markup isn't there, so callers needn't guard. */
function noop() {
  return {
    playOne() {},
    reset() {},
    invalidate() {},
    hide() {},
    destroy() {},
  };
}
